import { querySelectorOrThrow } from "../../utils/dom/querySelectorOrThrow";
import { IInitialisable } from "../../interfaces/IInitialisable";
import debounce from "lodash/debounce";

export class Accordion implements IInitialisable {
	private static debouncedTimeMs = 1000 / 14; /* 14 fps */
	private sharedEventDispatcher = document.createElement("div") as EventTarget;

	private readonly dispatcherEvents = {
		UpdateContent: "Accordion:Update:Content",
		UpdateTrigger: "Accordion:Update:Trigger",
	};

	private windowHelper = window;
	protected documentHelper = document;
	private querySelectorOrThrow = querySelectorOrThrow;

	constructor() {
		this.forceUnCheckAndDispatchUpdateEvents = debounce(this.forceUnCheckAndDispatchUpdateEvents, Accordion.debouncedTimeMs, { trailing: true });
		this.dispatchUpdateContentEvents = debounce(this.dispatchUpdateContentEvents, Accordion.debouncedTimeMs, { trailing: true });
		this.dispatchUpdateEvents = debounce(this.dispatchUpdateEvents, Accordion.debouncedTimeMs, { trailing: true });
	}

	// Tested
	public Init() {
		const components = document.querySelectorAll<HTMLElement>('[data-cmpt="accordion"].cmpt');
		if (components.length > 0) {
			components.forEach((component) => {
				this.setupComponent(component);
			});
			this.windowHelper.addEventListener("resize", () => this.dispatchUpdateContentEvents);
			this.dispatchUpdateEvents();
		}
	}

	// Tested
	private setupComponent(component: HTMLElement) {
		this.setupTriggerInteractions(component);
		this.setupContentHeightCalculations(component);
		component.classList.add("js_accordion__initialised");
	}

	// Tested
	private setupTriggerInteractions(component: HTMLElement) {
		const visibleTrigger = this.querySelectorOrThrow<HTMLLabelElement>(component, "label.js_accordion__trigger");
		const altVisibleTrigger = this.querySelectorOrThrow<HTMLLabelElement>(component, ".js_accordion__title");
		const checkboxOrRadioInput = this.querySelectorOrThrow<HTMLInputElement>(component, `#${visibleTrigger.htmlFor}`);
		const contentInner = this.querySelectorOrThrow<HTMLElement>(component, ".js_accordion__content__inner");

		this.sharedEventDispatcher.addEventListener(this.dispatcherEvents.UpdateTrigger, () => this.handleUpdateTrigger(visibleTrigger, checkboxOrRadioInput, contentInner));

		visibleTrigger.addEventListener("click", () => this.handleTriggerClicked(checkboxOrRadioInput));

		altVisibleTrigger.addEventListener("click", () => this.handleTitleClicked(checkboxOrRadioInput));
		altVisibleTrigger.style.cursor = "pointer";

		checkboxOrRadioInput.addEventListener("change", () => this.dispatchUpdateEvents());
	}

	// Tested
	private setupContentHeightCalculations(component: HTMLElement) {
		const contentOuter = this.querySelectorOrThrow<HTMLElement>(component, ".js_accordion__content__outer");
		const contentInner = this.querySelectorOrThrow<HTMLElement>(component, ".js_accordion__content__inner");

		this.sharedEventDispatcher.addEventListener(this.dispatcherEvents.UpdateContent, () => this.handleUpdateContent(contentOuter, contentInner));
	}

	// Tested
	private handleTitleClicked(checkboxOrRadioInput: HTMLInputElement) {
		checkboxOrRadioInput.checked = !checkboxOrRadioInput.checked;
		this.handleTriggerClicked(checkboxOrRadioInput);
	}

	// Tested
	private handleTriggerClicked(checkboxOrRadioInput: HTMLInputElement) {
		if (checkboxOrRadioInput.type === "radio" && checkboxOrRadioInput.checked && checkboxOrRadioInput.getAttribute("data-was-checked") === "true") {
			this.forceUnCheckAndDispatchUpdateEvents(checkboxOrRadioInput);
		} else {
			this.dispatchUpdateEvents();
		}
	}

	// Tested
	private forceUnCheckAndDispatchUpdateEvents(checkboxOrRadioInput: HTMLInputElement) {
		checkboxOrRadioInput.checked = false;
		this.dispatchUpdateEvents();
	}

	// Tested
	private dispatchUpdateContentEvents() {
		this.sharedEventDispatcher.dispatchEvent(new CustomEvent(this.dispatcherEvents.UpdateContent));
	}

	// Tested
	private dispatchUpdateEvents() {
		this.sharedEventDispatcher.dispatchEvent(new CustomEvent(this.dispatcherEvents.UpdateTrigger));
		this.sharedEventDispatcher.dispatchEvent(new CustomEvent(this.dispatcherEvents.UpdateContent));
	}

	// Tested
	private handleUpdateContent(contentOuter: HTMLElement, contentInner: HTMLElement) {
		const realContentHeight = contentInner.getBoundingClientRect().height;
		const additionalHeightPx = 100;
		contentOuter.style.maxHeight = `${realContentHeight + additionalHeightPx}px`;
	}

	// Tested
	private handleUpdateTrigger(visibleTrigger: HTMLLabelElement, checkboxOrRadioInput: HTMLInputElement, contentInner: HTMLElement) {
		this.setAttrToCheckedPropertyState(checkboxOrRadioInput);
		this.setAriaLabels(visibleTrigger, checkboxOrRadioInput, contentInner);
	}

	// Tested
	private setAttrToCheckedPropertyState(checkboxOrRadioInput: HTMLInputElement) {
		checkboxOrRadioInput.setAttribute("data-was-checked", checkboxOrRadioInput.checked ? "true" : "false");
	}

	// Tested
	private setAriaLabels(visibleTrigger: HTMLLabelElement, checkboxOrRadioInput: HTMLInputElement, contentInner: HTMLElement) {
		visibleTrigger.ariaHidden = "true";

		contentInner.ariaLive = "polite";
		contentInner.ariaHidden = checkboxOrRadioInput.checked ? "false" : "true";

		checkboxOrRadioInput.ariaLive = "polite";
		checkboxOrRadioInput.ariaChecked = checkboxOrRadioInput.checked ? "true" : "false";

		if (checkboxOrRadioInput.checked) {
			checkboxOrRadioInput.ariaLabel = checkboxOrRadioInput.getAttribute("data-aria-opened") || "Click to show less";
		} else {
			checkboxOrRadioInput.ariaLabel = checkboxOrRadioInput.getAttribute("data-aria-closed") || "Click to show more";
		}
	}
}
